import React, { createContext, useState, useCallback } from 'react'
import { Message, AIContextType } from './types'
import { useEditorSelection } from './hooks/useEditorSelection'
import { useEditHandler } from './hooks/useEditHandler'
import { useChatConversation } from './hooks/useChatConversation'

/**
 * AIContext provides the AI chat functionality throughout the application.
 * It manages the chat state, messages, and interactions with the AI API.
 */
export const AIContext = createContext<AIContextType | undefined>(undefined)

/**
 * ChatContextProvider - Central state management for the AI chat system
 * 
 * Architecture Overview:
 * ```
 * ChatContextProvider (this component)
 * ├── State Management
 * │   ├── messages: Message[]           - All chat messages
 * │   ├── showEditConfirmation: boolean - Controls edit dialog visibility
 * │   └── pendingEdit: Message          - Message with proposed edit
 * │
 * ├── Hooks Integration
 * │   ├── useEditorSelection
 * │   │   - Tracks text selection in BlockNote editor
 * │   │   - Provides: selectedText, selectedLineCount
 * │   │
 * │   ├── useEditHandler
 * │   │   - Retrieves current note content
 * │   │   - Applies AI-proposed edits to editor
 * │   │   - Provides: getNoteContent(), applyEdit()
 * │   │
 * │   └── useChatConversation
 * │       ├── Manages message sending/receiving
 * │       ├── Handles streaming AI responses
 * │       ├── Processes tool calls (note search)
 * │       └── Provides: sendMessage(), clearMessages(), isLoading, isSearching
 * │
 * └── Context Value (AIContextType)
 *     - Consumed via useAIChat() hook
 *     - Provides all state and functions to child components
 * ```
 * 
 * Data Flow:
 * 1. User Input → sendMessage() → API request → Streaming response
 * 2. AI proposes edit → setPendingEdit() → showEditConfirmation → User confirms
 * 3. applyEdit() → Updates BlockNote editor → Adds confirmation message
 * 4. Selected text → Automatically included in API requests
 * 
 * Key Features:
 * - Centralized state management for all chat functionality
 * - Integrates with BlockNote editor for document editing
 * - Handles streaming responses and tool calls
 * - Provides user feedback through system messages
 * 
 * Dependencies:
 * - useChatConversation: Handles API communication and streaming
 * - useEditHandler: Interfaces with BlockNote editor
 * - useEditorSelection: Monitors text selection in editor
 */
export function ChatContextProvider({ children }: { children: React.ReactNode }) {
  const [messages, setMessages] = useState<Message[]>([])
  const [showEditConfirmation, setShowEditConfirmation] = useState(false)
  const [pendingEdit, setPendingEdit] = useState<Message | undefined>(undefined)
  
  // Hook for tracking text selection in the editor
  const { selectedText, selectedLineCount } = useEditorSelection()
  
  // Hook for handling editor content retrieval and edit application
  const { getNoteContent, applyEdit: applyEditInternal } = useEditHandler()
  
  // Hook for managing the chat conversation flow (sending messages, receiving responses, streaming)
  const { sendMessage, clearMessages, isLoading, isSearching, webSearchStatus } = useChatConversation({
    messages,
    setMessages,
    getNoteContent,
    selectedText,
    setPendingEdit,
    setShowEditConfirmation
  })

  // Wrapper for applyEdit that adds user-friendly confirmation/error messages to the chat
  const applyEdit = useCallback((message: Message) => {
    applyEditInternal(
      message,
      () => {
        // On success
        const confirmMessage: Message = {
          id: `system-${Date.now().toString()}`,
          role: 'system',
          content: '✅ Edit applied successfully',
          timestamp: new Date(),
        }
        setMessages(previous => [...previous, confirmMessage])
      },
      (error) => {
        // On error
        const errorMessage: Message = {
          id: `system-${Date.now().toString()}`,
          role: 'system',
          content: '❌ Failed to apply edit: ' + error,
          timestamp: new Date(),
        }
        setMessages(previous => [...previous, errorMessage])
      }
    )
  }, [applyEditInternal])

  const value: AIContextType = {
    messages,
    sendMessage,
    clearMessages,
    isLoading,
    applyEdit,
    showEditConfirmation,
    setShowEditConfirmation,
    pendingEdit,
    setPendingEdit,
    selectedText,
    selectedLineCount,
    isSearching,
    webSearchStatus,
  }

  return <AIContext.Provider value={value}>{children}</AIContext.Provider>
}

